import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { UnleashFacade } from '@rd/core/facades/unleash-facade/unleash-facade.ts';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { StarNoticeModalComponent } from '../components/notice-modal/notice-modal.component';
import {
  StarNoticeModalService,
  STAR_NOTICE_MODAL_DIALOG_ID,
} from '../services/notice-modal/notice-modal.service';

/**
 * Puts a blocking notice modal over whichever page a logged-in member lands on,
 * until they acknowledge it. Nothing behind the modal is reachable while it is
 * up, so the route itself is always activated and the member stays where they
 * were. The acknowledgement is persisted per device, so this guard is a no-op on
 * every later visit from the same browser.
 */
@Injectable()
export class StarNoticeModalGuard implements CanActivate {
  constructor(
    private authFacade: AuthFacade,
    private dialogService: DialogService,
    private noticeModalService: StarNoticeModalService,
    private translateService: TranslateService,
    private unleashFacade: UnleashFacade,
  ) {}

  canActivate(): Observable<boolean> {
    return combineLatest([
      // `isLoggedIn$` coerces the pristine `null` to `false`, so wait on the
      // render state instead to avoid reading a premature "logged out".
      this.authFacade.authRenderState$,
      this.unleashFacade.isUnleashClientReady$,
    ]).pipe(
      filter(
        ([authRenderState, isUnleashReady]) =>
          authRenderState !== 'loading' && isUnleashReady,
      ),
      take(1),
      map(([authRenderState]) => {
        if (
          authRenderState === 'loggedIn' &&
          this.noticeModalService.isEnabled &&
          !this.noticeModalService.isAcknowledged
        ) {
          this.openNoticeModal();
        }

        return true;
      }),
    );
  }

  private openNoticeModal(): void {
    if (this.dialogService.currentDialogId === STAR_NOTICE_MODAL_DIALOG_ID) {
      return;
    }

    // Opening is deferred rather than awaited so a missing dialog host can never
    // hold up the navigation.
    this.noticeModalService.whenDialogHostReady().subscribe(() => {
      this.dialogService.open(StarNoticeModalComponent, {
        id: STAR_NOTICE_MODAL_DIALOG_ID,
        screenMode: 'center-md',
        ariaLabel: this.translateService.instant(
          'wl.star.notice_modal.aria_label',
        ),
        keepModalOnOverlayClick: true,
        keepModalOnEscapeKey: true,
        keepModalOnRouteChange: true,
      });
    });
  }
}
